import { CompactBand, BandTab, BandMintLink, BandButton, TrainBar } from '@/components/CompactBand';
import type { Mode, Tab } from '../types';
import { wholeMoney } from '../utils';
import { TRAINING } from '../fixtures';

function headline(toPostCount: number, toPostTotal: number, needsCount: number): React.ReactNode {
  if (toPostCount === 0) return 'Nothing is waiting to be posted.';
  const lead = `${String(toPostCount)} payments are ready to post, ${wholeMoney(toPostTotal)}.`;
  if (needsCount === 0) return lead;
  const need = needsCount === 1 ? 'One needs you.' : `${String(needsCount)} need you.`;
  return (
    <>
      {lead} <em>{need}</em>
    </>
  );
}

export function Header({
  view,
  mode,
  confirmed,
  toPostCount,
  toPostTotal,
  needsCount,
  postedCount,
  bankCount,
  matchedCount,
  uploading,
  onUpload,
  tabTo,
}: {
  view: Tab;
  mode: Mode;
  confirmed: number;
  toPostCount: number;
  toPostTotal: number;
  needsCount: number;
  postedCount: number;
  bankCount: number;
  matchedCount: number;
  /** A statement is being read: the button says "Reading…". */
  uploading: boolean;
  onUpload: () => void;
  tabTo: (t: Tab) => string;
}) {
  const uploadButton = (
    <span className="po-upload-wrap" title="Upload the bank statement as a CSV file">
      <BandButton variant="w" onClick={() => { if (!uploading) onUpload(); }}>
        {uploading ? 'Reading…' : 'Upload bank statement'}
      </BandButton>
    </span>
  );

  const right =
    mode === 'training' ? (
      <>
        <TrainBar confirmed={confirmed} goal={TRAINING.milestone} />
        <BandMintLink to="/settings/autopilot">How this works</BandMintLink>
        {uploadButton}
      </>
    ) : (
      <>
        <span>Autopilot on</span>
        <BandMintLink to="/settings/autopilot">Change</BandMintLink>
        {uploadButton}
      </>
    );

  const tabs = (
    <>
      <BandTab
        label="To post"
        meta={`${String(toPostCount)} · ${wholeMoney(toPostTotal)}`}
        active={view === 'to_post'}
        to={tabTo('to_post')}
      />
      <BandTab
        label="Posted"
        meta={`${String(postedCount)} this month`}
        active={view === 'posted'}
        to={tabTo('posted')}
      />
      <BandTab
        label="Bank deposits"
        meta={`${String(bankCount)} don't match`}
        active={view === 'bank'}
        to={tabTo('bank')}
      />
      <BandTab
        label="Matched"
        meta={`${String(matchedCount)} this month`}
        active={view === 'matched'}
        to={tabTo('matched')}
      />
    </>
  );

  return <CompactBand headline={headline(toPostCount, toPostTotal, needsCount)} right={right} tabs={tabs} />;
}
